/* -------------------------------------------------------------------------------------
Vuex module to manage waypoint data

Main components
---------------
mutations:
- CREATE_EMPTY_WAYP: 
- SET_WAYPOINTS: 
- SET_WAYPOINTS_LIST: 
- SET_WAYPOINTS_PER_TRACK: ACTION --> Is this required?
- SET_WAYPOINT: 

actions:
- createEmptyWaypoint: Pushes an empty waypoint object to store
- deleteWaypoint: Deletes current waypoint from DB & store
- fetchWaypoints: Gets all waypoints from the API and pushes them to the store -- ACTION: Unclear difference with fetchWaypointsList
- fetchWaypointsList: ??? --> ACTION: Unclear difference with fetchWaypoints
- fetchWaypoint: Get specific waypoint details from waypoints array and push it to vuex store
- insertWaypoint: Inserts new waypoint via API into DB
- updateWaypoint: Update the current waypoint in the DB 
- updateStoreWaypoints: Add or update waypoint in waypoints array --> ACTION: Use this instead of re-fetch of waypoints
- fetchWaypointsPerTrack: Fetch the waypoints per track stats from DB and push them to store --> ACTION: Move to separate stats store

getters:
- getHutById: Find given hut in huts
- getPeakById: Find given peak in peaks
- getWaypointById: Find given waypoint in waypoints
- wptHuts: Array with waypoints of type hut
- wptPeaks: Array with waypoints of type peak
- wptWaypoints: Array with waypoints of type waypoint

TASKS:
- ACTION: Unclear difference between fetchWaypoints and fetchWaypointsList
- Check dispatch('fetchWaypoints') // ACTION replace by removing record from array
- updateStoreWaypoints: Use this instead of re-fetch of waypoints
- fetchWaypointsPerTrack: Move to separate stats store
- Ensure that store waypoints contains all waypoints and that huts, peaks, wayps are only getters
---------------------------------------------------------------------------------------*/

// import tourdb library
import ApiService from '@/services/ApiService.js'

export const namespaced = true

export const state = {
  waypoints: []
}

export const mutations = {

  CREATE_EMPTY_WAYP(state, wayp) {
    state.wayp = wayp
  },

  SET_WAYPOINT_COORD(state, coord) {
    state.wayp.coordWGS84N = coord[0]
    state.wayp.coordWGS84E = coord[1]
  },

  SET_WAYPOINTS(state, waypoints) {
    state.waypoints = waypoints
  },

  SET_WAYPOINTS_LIST(state, waypointsList) {
    state.waypointsList = waypointsList
  },

  SET_WAYPOINTS_PER_TRACK(state, waypointsPerTrack) {
    state.waypointsPerTrack = waypointsPerTrack
  },

  SET_WAYPOINT(state, waypoint) {
    state.wayp = waypoint
  }
}

export const actions = {

  // STANDARD STORE ACTIONS
  // ----------------------

  // Pushes an empty waypoint object to store
  createEmptyWaypoint({ commit }) {

    let emptyWayp = {
      id: null,
      name: '',
      typeId: null,
      typeCode: '',
      typeName: '',
      areaId: null,
      areaCode: '',
      areaName: '',
      regionId: null,
      regionCode: '',
      regionName: '',
      canton: '',
      countryId: null,
      countryCode: '',
      countryName: '',
      altitude: 0,
      owner: '',
      website: '',
      remarks: '',
      UIAA4000: false,
      topOfCantonCode: null,
      coordLV3Est: 0,
      coordLV3Nord: 0,
      coordWGS84E: 0,
      coordWGS84N: 0,
      // create geojson with coordinates of Bundeshaus if new waypoint
      "geojson":{"type":"Feature","geometry":{"type":"Point","coordinates":[7.44420,46.94679]},"properties":{"name":"new"}} 
    }
    commit('CREATE_EMPTY_WAYP', emptyWayp)
  },

  // Deletes current waypoint from DB & store
  deleteWaypoint({ dispatch }) {

    // delete record
    ApiService.deleteWaypoint(this.state.waypoint.wayp.id)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The waypoint was successfully deleted'
        }
        dispatch('notification/add', notification, { root: true })
        // after the insert the participants array is fetched again

        dispatch('fetchWaypoints') // ACTION replace by removing record from array
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message:
            'There was an error deleting the waypoint (potentially referenced in other objects): ' +
            error.message
        }

        dispatch('notification/add', notification, { root: true })
      })
  },

  // Gets all waypoints from the API and pushes them to the store -- ACTION: Unclear difference with fetchWaypointsList
  fetchWaypoints({ commit, dispatch }) {

    ApiService.getWaypoints()
      .then(response => {
        commit(
          'SET_WAYPOINTS',
          response.data.waypoints
        )
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error getting the waypoints: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // ??? --> ACTION: Unclear difference with fetchWaypoints
  fetchWaypointsList({ commit, dispatch }) {

    ApiService.getWaypointsList()
      .then(response => {
        commit('SET_WAYPOINTS_LIST', response.data.waypointsList)
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message:
            'There was an error getting the waypointsList: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Get specific waypoint details from waypoints array and push it to vuex store
  fetchWaypoint({ commit, getters }, { waypoint }) {
    
    commit('SET_WAYPOINT', waypoint) // push waypoint into vuex
  },

  // Inserts new waypoint via API into DB
  insertWaypoint({ dispatch }) {
    // insert new wayp via API

    // first update the name field
    let altitude = this.state.waypoint.wayp.altitude
      ? ' ' + this.state.waypoint.wayp.altitude + 'm'
      : ''
    let canton = this.state.waypoint.wayp.canton
      ? ' ' + this.state.waypoint.wayp.canton
      : ''

    this.state.waypoint.wayp.fullName =
      this.state.waypoint.wayp.name + altitude + canton

    ApiService.insertWaypoint(this.state.waypoint.wayp)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The waypoint was successfully inserted'
        }
        dispatch('notification/add', notification, { root: true })
        // after the insert the waypoint array is fetched again
        dispatch('fetchWaypoints')
      })
      .catch(error => {
        const notification = {
          type: 'error',
          message:
            'There was an error inserting the waypoints: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Update the current waypoint in the DB 
  updateWaypoint({ dispatch }, waypId) {
    // update corresponding wayp via API
    ApiService.updateWayp(waypId, this.state.waypoint.wayp)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The participant was successfully saved'
        }
        dispatch('notification/add', notification, { root: true })
      })
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error saving the participant: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  updateWaypointCoord({ commit }, coordinates) {
    commit('SET_WAYPOINT_COORD', coordinates) // set user data in vuex
  },

  // Add or update waypoint in waypoints array --> : Use this instead of re-fetACTIONch of waypoints
  updateStoreWaypoints({ commit }, { waypId }) {

    let curWayps = JSON.parse(JSON.stringify(this.state.waypoint.waypoints))
    let newWayps = []

    if (waypId) {
      // if waypoint id exists it's an update operation

      curWayps.forEach(record => {
        // loop through existing participants array
      
        if (record.id == waypId) {
          // if current participant is the changed participant
          newWayps.push(JSON.parse(JSON.stringify(this.state.waypoint.wayp))) // the participant is being replaced
        } else {
          newWayps.push(record) // the existing participant is pushed out
        }
      })

    } else {
      // if no participantId exists, it's an insert operation
      curWayps.push(JSON.parse(JSON.stringify(this.state.waypoint.wayp)))
      newWayps = curWayps
    }
    commit('SET_WAYPOINTS', newWayps)
  },

  // STATS ACTIONS
  // -------------

  // Fetch the waypoints per track stats from DB and push them to store --> ACTION: Move to separate stats store
  fetchWaypointsPerTrack({ commit, dispatch }) {
    ApiService.getWaypointsPerTrack()
      .then(response => {
        commit('SET_WAYPOINTS_PER_TRACK', response.data.waypointsPerTrack)
      })
      .catch(error => {
        const notification = {
          type: 'error',
          message:
            'There was an error getting the waypointsPerTrack: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },
}

export const getters = {

  // find given hut in huts
  getHutById: state => id => {
    
    let wayps = JSON.parse(JSON.stringify(state.huts))
    return wayps.find(wayp => wayp.id === id)
  },

  // find given peak in peaks
  getPeakById: state => id => {
    let wayps = JSON.parse(JSON.stringify(state.peaks))
    return wayps.find(wayp => wayp.id === id)
  },
  
  // find given waypoint in waypoints
  getWaypointById: state => id => {
    let wayps = JSON.parse(JSON.stringify(state.waypoints))
    return wayps.find(wayp => wayp.id === id)
  },
  
  // Array with waypoints of type hut
  wptHuts: state => {
    return state.waypoints.filter(
      waypoints => waypoints.typeCode == 'hu'
    )
  },

  // Array with waypoints of type peak
  wptPeaks: state => {
    return state.waypoints.filter(
      waypoints => waypoints.typeCode == 'gi'
    )
  },

  // Array with waypoints of type waypoint
  wptWaypoints: state => {
    return state.waypoints.filter(
      waypoints => waypoints.typeCode == 'wpt'
    )
  },
}
